import React from "react";
import {
    Box, Stack, Flex, Text, Input, Textarea, IconButton, Icon, Tooltip, useColorModeValue,
} from "@chakra-ui/react";
import { MdAddCircleOutline, MdDeleteOutline } from "react-icons/md";
import { ItemsEditor } from "./ItemsEditor";

/**
 * Renders the header row of a subsection with number label and remove button.
 * @param {object} props
 * @param {string} props.number - Subsection number
 * @param {function} props.onRemove - Callback to remove the subsection
 * @returns {React.ReactElement}
 */
function SubsectionHeader({ number, onRemove }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex justify="space-between" align="center" mb="8px">
            <Text fontSize="0.75rem" fontWeight="600" color={labelColor}>Subsection {number}</Text>
            <Tooltip label="Remove subsection">
                <IconButton aria-label="Supprimer la sous-section" size="xs" variant="ghost" color="red.400"
                    icon={<Icon as={MdDeleteOutline} />} onClick={onRemove} />
            </Tooltip>
        </Flex>
    );
}

/**
 * Renders editable fields for one subsection (number, subtitle, content, items).
 * @param {object} props
 * @param {object} props.subsection - Subsection data
 * @param {function} props.onChange - Callback with (field, value) when a field changes
 * @param {function} props.onRemove - Callback to remove this subsection
 * @param {function} props.onAddItem - Callback with (itemText) to add an item
 * @param {function} props.onRemoveItem - Callback with (itemIndex) to remove an item
 * @returns {React.ReactElement}
 */
function SubsectionBlock({ subsection, onChange, onRemove, onAddItem, onRemoveItem }) {
    const boxBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    return (
        <Box p="12px" mb="8px" borderRadius="8px" bg={boxBg}>
            <SubsectionHeader number={subsection.number} onRemove={onRemove} />
            <Stack spacing="8px">
                <Stack direction="row" spacing="8px">
                    <Input size="sm" w="80px" placeholder="Number" value={subsection.number}
                        onChange={(e) => onChange("number", e.target.value)} />
                    <Input size="sm" placeholder="Subtitle" value={subsection.subtitle}
                        onChange={(e) => onChange("subtitle", e.target.value)} />
                </Stack>
                <Textarea size="sm" rows={2} placeholder="Content" value={subsection.content}
                    onChange={(e) => onChange("content", e.target.value)} />
                <ItemsEditor items={subsection.items || []} onAdd={onAddItem} onRemove={onRemoveItem} />
            </Stack>
        </Box>
    );
}

/**
 * Renders the main section fields: number, title, and introductory text.
 * @param {object} props
 * @param {object} props.section - Section data
 * @param {function} props.onFieldChange - Handler for field changes (field, value)
 * @returns {React.ReactElement}
 */
function SectionMainFields({ section, onFieldChange }) {
    return (
        <Stack spacing="12px" mb="16px">
            <Stack direction="row" spacing="8px">
                <Input size="sm" w="80px" placeholder="Number" value={section.number}
                    onChange={(e) => onFieldChange("number", e.target.value)} />
                <Input size="sm" placeholder="Title" value={section.title}
                    onChange={(e) => onFieldChange("title", e.target.value)} />
            </Stack>
            <Textarea size="sm" rows={2} placeholder="Introductory text" value={section.text || ""}
                onChange={(e) => onFieldChange("text", e.target.value)} />
        </Stack>
    );
}

/**
 * Renders the subsection list header with count label and add button.
 * @param {object} props
 * @param {number} props.count - Number of subsections
 * @param {function} props.onAdd - Callback to add a new subsection
 * @returns {React.ReactElement}
 */
function SubsectionListHeader({ count, onAdd }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex justify="space-between" align="center" mb="8px">
            <Text fontSize="0.75rem" fontWeight="600" textTransform="uppercase" color={labelColor}>
                Subsections ({count})
            </Text>
            <Tooltip label="Add subsection">
                <IconButton aria-label="Ajouter une sous-section" size="xs" variant="ghost"
                    icon={<Icon as={MdAddCircleOutline} />} onClick={onAdd} />
            </Tooltip>
        </Flex>
    );
}

/**
 * Updates a subsection field within the section's subsections array.
 * @param {object} section - Current section data
 * @param {number} subIdx - Subsection index
 * @param {string} field - Field name to update
 * @param {*} value - New value
 * @returns {Array} Updated subsections array
 */
function updateSubField(section, subIdx, field, value) {
    return section.subsections.map((s, i) => i === subIdx ? { ...s, [field]: value } : s);
}

/**
 * Adds an item to a subsection's items array.
 * @param {object} section - Current section data
 * @param {number} subIdx - Subsection index
 * @param {string} itemText - Item text to add
 * @returns {Array} Updated subsections array
 */
function addSubItem(section, subIdx, itemText) {
    return section.subsections.map((s, i) =>
        i === subIdx ? { ...s, items: [...(s.items || []), itemText] } : s
    );
}

/**
 * Removes an item from a subsection's items array.
 * @param {object} section - Current section data
 * @param {number} subIdx - Subsection index
 * @param {number} itemIdx - Item index to remove
 * @returns {Array} Updated subsections array
 */
function removeSubItem(section, subIdx, itemIdx) {
    return section.subsections.map((s, i) =>
        i === subIdx ? { ...s, items: s.items.filter((_, k) => k !== itemIdx) } : s
    );
}

/**
 * Renders a full section editor with its fields and nested subsections.
 * Provides add/remove/edit for subsections within the section.
 * @param {object} props
 * @param {object} props.section - Section data {number, title, text, subsections}
 * @param {function} props.onSectionChange - Callback with updated section object
 * @returns {React.ReactElement}
 */
function SectionEditor({ section, onSectionChange }) {
    /** @param {string} field @param {*} value */
    const handleFieldChange = (field, value) => onSectionChange({ ...section, [field]: value });

    /** Appends a new empty subsection to the section. */
    const handleAddSubsection = () => {
        const newSub = { number: `${section.number}.${(section.subsections?.length || 0) + 1}`, subtitle: "", content: "", items: [] };
        onSectionChange({ ...section, subsections: [...(section.subsections || []), newSub] });
    };

    return (
        <Box>
            <SectionMainFields section={section} onFieldChange={handleFieldChange} />
            <SubsectionListHeader count={section.subsections?.length || 0} onAdd={handleAddSubsection} />
            {(section.subsections || []).map((sub, idx) => (
                <SubsectionBlock key={idx} subsection={sub}
                    onChange={(field, value) => onSectionChange({ ...section, subsections: updateSubField(section, idx, field, value) })}
                    onRemove={() => onSectionChange({ ...section, subsections: section.subsections.filter((_, i) => i !== idx) })}
                    onAddItem={(text) => onSectionChange({ ...section, subsections: addSubItem(section, idx, text) })}
                    onRemoveItem={(itemIdx) => onSectionChange({ ...section, subsections: removeSubItem(section, idx, itemIdx) })}
                />
            ))}
        </Box>
    );
}

export default SectionEditor;
